import React from "react";
import Link from "next/link";

type ColorKey = "blue" | "pink" | "yellow";

interface ButtonProps {
  label: string;
  color?: ColorKey | string;
  icon?: string | React.ReactNode;
  iconPosition?: "left" | "right" | "none";
  textColor?: string;
  onClick?: () => void;
  className?: string;
  href?: string;
}

const COLOR_MAP: Record<ColorKey, { bg: string; hover: string; text: string; shadow: string }> = {
  blue:   { bg: "bg-[#117DD4]", hover: "hover:bg-[#117DD4]/90", text: "text-white",       shadow: "shadow-lg hover:shadow-blue-200/30"   },
  pink:   { bg: "bg-[#F87171]", hover: "hover:bg-[#F87171]/90", text: "text-white",       shadow: "shadow-lg hover:shadow-pink-200/30"   },
  yellow: { bg: "bg-[#FBBF24]", hover: "hover:bg-[#FBBF24]/90", text: "text-[#0A2540]",  shadow: "shadow-lg hover:shadow-yellow-200/30" },
};

const DEFAULT_STYLES = { bg: "", hover: "", text: "", shadow: "" };

const Icon = ({ icon, label }: { icon: string | React.ReactNode; label: string }) =>
  typeof icon === "string" ? (
    <img src={icon} alt={`${label} icon`} className="w-5 h-5" />
  ) : (
    <span className="w-5 h-5 flex items-center justify-center">{icon}</span>
  );

const Button: React.FC<ButtonProps> = ({
  label,
  color = "blue",
  icon,
  iconPosition = "left",
  textColor,
  onClick,
  className = "",
  href,
}) => {
  const { bg, hover, text, shadow } = (COLOR_MAP[color as ColorKey] ?? { ...DEFAULT_STYLES, bg: color, text: textColor ?? "" });

  const inner = (
    <span
      className={`flex items-center justify-center gap-2 font-bold py-3 px-4 text-lg transition-all cursor-pointer ${bg} ${hover} ${text} ${shadow} ${className}`}
      style={textColor ? { color: textColor } : undefined}
    >
      {icon && iconPosition === "left"  && <Icon icon={icon} label={label} />}
      {label}
      {icon && iconPosition === "right" && <Icon icon={icon} label={label} />}
    </span>
  );

  if (href) {
    return (
      <Link href={href} onClick={onClick} className="w-full">
        {inner}
      </Link>
    );
  }

  return (
    <button className="w-full" onClick={onClick}>
      {inner}
    </button>
  );
};

export default Button;